/**
 * useContactForm — owns the ContactEditModal edit/create form-field state
 * cluster, extracted verbatim from `ContactEditModal.tsx` (#3237 Pass-2).
 *
 * This is a single CONTIGUOUS relocation of the state-declaration run
 * (identity/CRM/enrichment fields + freeform mode + the relationship-assist /
 * draft / mobile-activity / right-tab UI-state that was interleaved in the
 * same block) plus the mobile-keyboard `visualViewport` effect. Because the
 * whole run moves together and the hook is called unconditionally at the same
 * spot every render, the React hook-call sequence is byte-for-byte identical
 * to before — no Rules-of-Hooks change.
 *
 * The orchestrator keeps the contact→form init `useEffect` shell (delegating
 * to `syncContactToForm`); this hook only owns the state atoms it reads/writes.
 * `doNotBulkEnrichLocal` / `enrichmentReviewedAtLocal` keep their lazy
 * initializers reading the passed `contact` exactly as before.
 */

import { useState, useRef, useEffect } from 'react';
import { DEFAULT_LABEL_COLOR } from '../../../shared/colors';
import type { RelationshipState } from '../../RelationshipAssistPanel';
import type { DraftVariant } from '../../DraftVariantSelector';
import type { Activity, ContactObject } from '../lib/types';

export function useContactForm(params: {
  isNew: boolean | undefined;
  contact: ContactObject | null | undefined;
  isMobile: boolean;
}) {
  const { isNew, contact, isMobile } = params;

  // Simple contact form fields (new)
  // View/edit toggle — existing contacts open in view mode, new contacts in edit mode
  const [isViewMode, setIsViewMode] = useState(!isNew);
  // Freeform mode — natural language input for new contacts
  const [freeformMode, setFreeformMode] = useState(isNew);
  const [freeformText, setFreeformText] = useState('');
  const [freeformParsing, setFreeformParsing] = useState(false);
  const [freeformParsed, setFreeformParsed] = useState<Record<string, any> | null>(null);

  // Gmail people search for new contacts
  const [gmailResults, setGmailResults] = useState<Array<{ email: string; name: string; photoUrl: string | null; score: number }>>([]);
  const googleContactsRef = useRef<Array<{ name?: string; email?: string; phone?: string; organization?: string; title?: string; photoUrl?: string }>>([]);
  const gmailSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [xHandle, setXHandle] = useState('');

  // Core contact fields
  const [name, setName] = useState('');
  // Display-name override from the contact record (null = use auto-cased name)
  const [displayNameOverride, setDisplayNameOverride] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  // Secondary/tertiary emails beyond the primary (`email`). The editor renders
  // these with a remove (×) control; "+ Add email" appends a blank one.
  const [additionalEmails, setAdditionalEmails] = useState<string[]>([]);
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [lists, setLists] = useState<string[]>([]);
  const [stage, setStage] = useState('');
  const [summary, setSummary] = useState('');

  // CRM fields
  const [dealValue, setDealValue] = useState('');
  const [probability, setProbability] = useState('');
  const [activities, setActivities] = useState<Activity[]>([]);
  const optimisticSendRef = useRef(false);
  const [label, setLabel] = useState('');
  const [labelColor, setLabelColor] = useState(DEFAULT_LABEL_COLOR);
  const [nextStepAction, setNextStepAction] = useState('');
  const [nextStepDueAt, setNextStepDueAt] = useState('');
  // Track last saved follow-up to detect changes and prevent duplicate notes
  const [lastSavedFollowUp, setLastSavedFollowUp] = useState<{ action: string; dueAt: string } | null>(null);

  // AI Relationship Assist state
  const [relationshipState, setRelationshipState] = useState<RelationshipState | null>(null);
  const [relStateLoading, setRelStateLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [draftVariants, setDraftVariants] = useState<DraftVariant[]>([]);
  const [draftLoading, setDraftLoading] = useState(false);
  const [showDraftSelector, setShowDraftSelector] = useState(false);
  const [composeFromDraft, setComposeFromDraft] = useState<{ subject: string; body: string } | null>(null);
  const [showMobileActivity, setShowMobileActivity] = useState(false);
  const [mobileKeyboardHeight, setMobileKeyboardHeight] = useState(0);

  // Public activity tab (right column)
  const [rightTab, setRightTab] = useState<'activity' | 'public' | 'insights'>('activity');
  const [publicActivity, setPublicActivity] = useState<Array<{ date?: string; text: string; sourceUrl?: string }> | null>(null);
  const [publicActivityLoading, setPublicActivityLoading] = useState(false);

  // Email insights tab
  const [emailInsights, setEmailInsights] = useState<{
    tone?: string; dynamic?: string; topics?: string[];
    trajectory?: string; openLoops?: string[]; summary?: string;
  } | null>(null);
  const [emailInsightsLoading, setEmailInsightsLoading] = useState(false);

  // Track keyboard height via visualViewport for mobile activity sheet
  useEffect(() => {
    if (!isMobile || !showMobileActivity) { setMobileKeyboardHeight(0); return; }
    const vv = window.visualViewport;
    if (!vv) return;
    const onResize = () => {
      const kbHeight = window.innerHeight - vv.height;
      setMobileKeyboardHeight(kbHeight > 50 ? kbHeight : 0);
    };
    vv.addEventListener('resize', onResize);
    return () => vv.removeEventListener('resize', onResize);
  }, [isMobile, showMobileActivity]);

  // Enrichment fields
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [contactTitle, setContactTitle] = useState('');
  const [firm, setFirm] = useState('');
  const [location, setLocation] = useState('');
  const [estimatedAum, setEstimatedAum] = useState('');
  const [accreditedStatus, setAccreditedStatus] = useState('unknown');
  const [contactSource, setContactSource] = useState('');
  const [enrichmentTags, setEnrichmentTags] = useState<string[]>([]);

  // AI Enrichment lookup state
  const [enrichLoading, setEnrichLoading] = useState(false);
  const [enrichUnsaved, setEnrichUnsaved] = useState(false);
  const [enrichHintsPrompt, setEnrichHintsPrompt] = useState(false);
  const [enrichHints, setEnrichHints] = useState('');
  // enrichResult/enrichError values were only read by the deleted dead EnrichmentPanel;
  // the setters remain live (handleEnrich, reset paths). Keep state, drop unused value binding.
  const [, setEnrichResult] = useState<{
    confidence: string;
    fields: Record<string, any>;
    rawSummary: string;
  } | null>(null);
  const [, setEnrichError] = useState<string | null>(null);

  const [enrichmentSummary, setEnrichmentSummary] = useState('');
  const [enrichmentSkills, setEnrichmentSkills] = useState<string[]>([]);
  const [enrichmentConfidence, setEnrichmentConfidence] = useState('');
  const [enrichmentAttemptedAt, setEnrichmentAttemptedAt] = useState('');
  // Optimistic mirror of attributes.doNotBulkEnrich so the toggle badge
  // reflects the new state immediately. The parent's _stayOpen save path
  // doesn't refetch the contact, so without this the badge would stay on
  // the old value until the modal is closed+reopened.
  const [doNotBulkEnrichLocal, setDoNotBulkEnrichLocal] = useState<boolean>(
    () => {
      const c = contact as Record<string, unknown> | undefined;
      const attrs = (c?.attributes as Record<string, unknown> | undefined) || {};
      // API flattens attributes to top-level on the contact list endpoint,
      // so check both shapes.
      return c?.doNotBulkEnrich === true || attrs.doNotBulkEnrich === true;
    },
  );
  // Same pattern for enrichmentReviewedAt — the "Confirm identity" /
  // "Skip AI" buttons set it, and without an optimistic mirror the
  // low-confidence banner sticks around until close+reopen.
  const [enrichmentReviewedAtLocal, setEnrichmentReviewedAtLocal] = useState<string>(
    () => {
      const c = contact as Record<string, unknown> | undefined;
      const attrs = (c?.attributes as Record<string, unknown> | undefined) || {};
      const fromTop = typeof c?.enrichmentReviewedAt === 'string' ? c.enrichmentReviewedAt as string : '';
      const fromAttrs = typeof attrs.enrichmentReviewedAt === 'string' ? attrs.enrichmentReviewedAt as string : '';
      return fromTop || fromAttrs;
    },
  );

  return {
    isViewMode, setIsViewMode,
    freeformMode, setFreeformMode,
    freeformText, setFreeformText,
    freeformParsing, setFreeformParsing,
    freeformParsed, setFreeformParsed,
    gmailResults, setGmailResults,
    googleContactsRef,
    gmailSearchTimer,
    firstName, setFirstName,
    lastName, setLastName,
    xHandle, setXHandle,
    name, setName,
    displayNameOverride, setDisplayNameOverride,
    email, setEmail,
    additionalEmails, setAdditionalEmails,
    phone, setPhone,
    company, setCompany,
    lists, setLists,
    stage, setStage,
    summary, setSummary,
    dealValue, setDealValue,
    probability, setProbability,
    activities, setActivities,
    optimisticSendRef,
    label, setLabel,
    labelColor, setLabelColor,
    nextStepAction, setNextStepAction,
    nextStepDueAt, setNextStepDueAt,
    lastSavedFollowUp, setLastSavedFollowUp,
    relationshipState, setRelationshipState,
    relStateLoading, setRelStateLoading,
    analyzing, setAnalyzing,
    draftVariants, setDraftVariants,
    draftLoading, setDraftLoading,
    showDraftSelector, setShowDraftSelector,
    composeFromDraft, setComposeFromDraft,
    showMobileActivity, setShowMobileActivity,
    mobileKeyboardHeight, setMobileKeyboardHeight,
    rightTab, setRightTab,
    publicActivity, setPublicActivity,
    publicActivityLoading, setPublicActivityLoading,
    emailInsights, setEmailInsights,
    emailInsightsLoading, setEmailInsightsLoading,
    linkedinUrl, setLinkedinUrl,
    contactTitle, setContactTitle,
    firm, setFirm,
    location, setLocation,
    estimatedAum, setEstimatedAum,
    accreditedStatus, setAccreditedStatus,
    contactSource, setContactSource,
    enrichmentTags, setEnrichmentTags,
    enrichLoading, setEnrichLoading,
    enrichUnsaved, setEnrichUnsaved,
    enrichHintsPrompt, setEnrichHintsPrompt,
    enrichHints, setEnrichHints,
    setEnrichResult,
    setEnrichError,
    enrichmentSummary, setEnrichmentSummary,
    enrichmentSkills, setEnrichmentSkills,
    enrichmentConfidence, setEnrichmentConfidence,
    enrichmentAttemptedAt, setEnrichmentAttemptedAt,
    doNotBulkEnrichLocal, setDoNotBulkEnrichLocal,
    enrichmentReviewedAtLocal, setEnrichmentReviewedAtLocal,
  };
}
